import React from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { addTagToUserThunk } from '../settings/settingsSlice';
import type { RootState } from '../../reducers';

type P = () => boolean;
interface IDismissableBannerProps {
  id: string;
  title: string;
  body: string;
}
interface BannerConfig {
  id: string;
  title: string;
  body: string;
  predicate: P;
}
const DismissableBanner: React.FC<IDismissableBannerProps> = (props) => {
  const { id, title, body } = props;
  const dispatch = useDispatch();
  const userTags = useSelector((state: RootState) => state.settings.user.tags);

  const leaveMeAloneKey = `userAction_${id}`;
  const now = new Date();

  const lastDismissed = userTags[leaveMeAloneKey]
    ? new Date(userTags[leaveMeAloneKey])
    : null;
  let isWithin24Hours = false;
  if (lastDismissed) {
    const oneDayLater = new Date(lastDismissed);
    oneDayLater.setDate(oneDayLater.getDate() + 1);
    isWithin24Hours = now < oneDayLater;
  }

  if (isWithin24Hours) {
    return null;
  }

  const handleDismiss = () => {
    dispatch(
      addTagToUserThunk({
        key: leaveMeAloneKey,
        value: new Date().toISOString(),
      })
    );
  };

  return (
    <Alert severity="warning" onClose={handleDismiss} title={title}>
      <AlertTitle sx={{ fontWeight: 700 }}>{title}</AlertTitle>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {body}
      </Typography>
    </Alert>
  );
};

const EncourageUserActionBanner: React.FC = () => {
  const { user } = useSelector((state: RootState) => ({
    user: state?.settings.user,
  }));

  const { reconnect: reconnectGoogle } =
    user.integrations?.google?.calendar || {};
  const { reconnect: reconnectMicrosoft } =
    user.integrations?.microsoft?.calendar || {};
  const { reconnect: reconnectZoom } =
    user.integrations?.zoomPlatform?.generalApp || {};

  const config: BannerConfig[] = [
    {
      id: 'reconnect_google_calendar',
      body:
        'We need account authorization to access calendar so we can automatically join and record your meetings. Go to Settings > Meeting Bot to connect.',
      title: 'Reconnect Google Calendar',
      predicate: () => Boolean(reconnectGoogle),
    },
    {
      id: 'reconnect_microsoft_calendar',
      body:
        'We need account authorization to access calendar so we can automatically join and record your meetings. Go to Settings > Meeting Bot to connect.',
      title: 'Reconnect Microsoft Teams Calendar',
      predicate: () => Boolean(reconnectMicrosoft),
    },
    {
      id: 'reconnect_zoom_meetings',
      body:
        'We need account authorization to access Zoom so we can automatically join and record your meetings. Go to Settings > Meeting Bot to connect.',
      title: 'Reconnect Zoom Meetings',
      predicate: () => Boolean(reconnectZoom),
    },
  ].filter((c) => c.predicate());

  if (config.length === 0) return null;

  return (
    <>
      {config.map((banner) => {
        return (
          <DismissableBanner
            key={banner.id}
            id={banner.id}
            title={banner.title}
            body={banner.body}
          />
        );
      })}
    </>
  );
};
export default EncourageUserActionBanner;
